import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";

import "@material/web/progress/circular-progress.js";
import "@material/web/button/filled-button.js";
import "@material/web/button/outlined-button.js";
import "@material/web/button/text-button.js";
import "@material/web/textfield/outlined-text-field.js";
import "@material/web/select/outlined-select.js";
import "@material/web/select/select-option.js";
import "@material/web/checkbox/checkbox.js";
import "@material/web/switch/switch.js";
import "@material/web/icon/icon.js";
import type { MdSwitch } from "@material/web/switch/switch.js";

import "../components/app-shell";
import { api, ApiError } from "../lib/api";
import { sharedStyles } from "../styles/shared";
import { inputValue, inputChecked, errorMessage } from "../lib/dom";
import { icon } from "../lib/icons";
import { toast } from "../lib/toast";
import { AuthController } from "../lib/auth-controller";
import type { Product } from "../lib/types";

// Mirrors internal/modules/assistance/domain.go. The types live here rather
// than in lib/types.ts because no other page reads them.
type AssistanceMode = "link" | "form";

const MODE_LABELS: Record<AssistanceMode, string> = {
  link: "Link — hand over to the programme's own page",
  form: "Form — collect answers on the tap page and relay them",
};

// The render allowlist from domain.go (fieldTypes). An unknown type falls back
// to text server-side, so only these are offered.
const FIELD_TYPES = [
  "text",
  "email",
  "tel",
  "number",
  "date",
  "textarea",
  "select",
] as const;
type AssistanceFieldType = (typeof FIELD_TYPES)[number];

interface AssistanceField {
  key: string;
  label: string;
  type: string;
  required: boolean;
  options?: string[];
  help?: string;
}

// The public Program shape (assistance/domain.go). Note what is absent:
// submitUrl and submitToken never come back from the API — they live only in
// the record, readable only by the relay.
interface AssistanceProgram {
  id: string;
  orgId: string;
  productId?: string;
  enabled: boolean;
  markets: string[] | null;
  title: string;
  body: string;
  mode: AssistanceMode;
  programUrl?: string;
  phone?: string;
  sponsor?: string;
  privacyUrl?: string;
  fields?: AssistanceField[];
}

// One row of the form-mode field builder. Options are edited as a comma list
// and split on save.
interface FieldDraft {
  key: string;
  label: string;
  type: AssistanceFieldType;
  required: boolean;
  optionsText: string;
  help: string;
}

function emptyFieldDraft(): FieldDraft {
  return {
    key: "",
    label: "",
    type: "text",
    required: false,
    optionsText: "",
    help: "",
  };
}

// Patient/customer assistance ("savings programme") configuration. Org-admin
// only — the server enforces it (requireOrgAdmin in assistance/service.go);
// other members get a read-down explanation instead of a broken table.
//
// This page holds CONFIGURATION only. Applications are relayed straight to the
// manufacturer's endpoint and never stored here — there is no submissions
// table, by design (see internal/modules/assistance/AGENTS.md).
@customElement("assistance-page")
export class AssistancePage extends LitElement {
  static styles = [
    sharedStyles,
    css`
      .editor {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }
      .editor h3 {
        margin: 8px 0 0;
        font: 500 14px/1.2 var(--font-plain, sans-serif);
        color: var(--md-sys-color-on-surface-variant);
      }
      .enable-row {
        display: flex;
        align-items: center;
        gap: 12px;
      }
      .enable-row span {
        font: 400 14px/1.4 var(--font-plain, sans-serif);
      }
      /* The markets gate is the one setting that silently hides a live
         programme, so it gets a callout, not a bare text field. */
      .markets-callout {
        background: var(--md-sys-color-tertiary-container);
        color: var(--md-sys-color-on-tertiary-container);
        border-radius: 12px;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      .markets-callout p {
        margin: 0;
        font-size: 0.8125rem;
      }
      .markets-callout md-outlined-text-field {
        --md-outlined-text-field-container-shape: 8px;
        background: var(--md-sys-color-surface);
        border-radius: 8px;
      }
      .field-card {
        border: 1px solid var(--md-sys-color-outline-variant);
        border-radius: 12px;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      .field-card__head {
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .field-card__head b {
        flex: 1;
        font: 500 13px/1.2 var(--font-plain, sans-serif);
        color: var(--md-sys-color-on-surface-variant);
      }
      .checkbox-row {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font: 400 14px/1.4 var(--font-plain, sans-serif);
        cursor: pointer;
      }
      .editor-actions {
        display: flex;
        gap: 8px;
        justify-content: flex-end;
        flex-wrap: wrap;
      }
      .hint {
        margin: 0;
        font-size: 0.8125rem;
        color: var(--md-sys-color-on-surface-variant);
      }
    `,
  ];

  private auth = new AuthController(this);

  @state() private programs: AssistanceProgram[] = [];
  @state() private products: Product[] = [];
  @state() private loading = true;
  @state() private error = "";
  @state() private forbidden = false;

  // Editor state. editorOpen + editingId ("" = create) drive the card; the
  // f* fields mirror ProgramInput (assistance/service.go).
  @state() private editorOpen = false;
  @state() private editingId = "";
  @state() private fProduct = "";
  @state() private fEnabled = false;
  @state() private fMarkets = "";
  @state() private fTitle = "";
  @state() private fBody = "";
  @state() private fMode: AssistanceMode = "link";
  @state() private fProgramUrl = "";
  @state() private fPhone = "";
  @state() private fSponsor = "";
  @state() private fPrivacyUrl = "";
  @state() private fFields: FieldDraft[] = [];
  @state() private fSubmitUrl = "";
  @state() private fSubmitToken = "";
  @state() private saving = false;
  @state() private saveError = "";

  connectedCallback(): void {
    super.connectedCallback();
    void this.load();
  }

  // Configuration is an org-admin concern, like the server says
  // (requireOrgAdmin accepts org_admin or platform_operator).
  private get canManage(): boolean {
    return (
      this.auth.hasRole("org_admin") || this.auth.hasRole("platform_operator")
    );
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.error = "";
    this.forbidden = false;
    try {
      // Programmes and the product catalog (for the optional product scope)
      // are independent reads, issued together.
      const [programs, products] = await Promise.all([
        api.get<AssistanceProgram[] | null>("/assistance/programs"),
        api.get<Product[]>("/catalog/products"),
      ]);
      this.programs = programs ?? [];
      this.products = products;
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        this.forbidden = true;
      } else {
        this.error = errorMessage(err, "Failed to load assistance programmes.");
      }
    } finally {
      this.loading = false;
    }
  }

  private productName(productId?: string): string {
    if (!productId) return "Org-wide";
    return this.products.find((p) => p.ID === productId)?.Name ?? productId;
  }

  private openCreate(): void {
    this.editingId = "";
    this.fProduct = "";
    this.fEnabled = false;
    this.fMarkets = "";
    this.fTitle = "";
    this.fBody = "";
    this.fMode = "link";
    this.fProgramUrl = "";
    this.fPhone = "";
    this.fSponsor = "";
    this.fPrivacyUrl = "";
    this.fFields = [];
    this.fSubmitUrl = "";
    this.fSubmitToken = "";
    this.saveError = "";
    this.editorOpen = true;
  }

  private openEdit(p: AssistanceProgram): void {
    this.editingId = p.id;
    this.fProduct = p.productId ?? "";
    this.fEnabled = p.enabled;
    this.fMarkets = (p.markets ?? []).join(", ");
    this.fTitle = p.title;
    this.fBody = p.body;
    this.fMode = p.mode;
    this.fProgramUrl = p.programUrl ?? "";
    this.fPhone = p.phone ?? "";
    this.fSponsor = p.sponsor ?? "";
    this.fPrivacyUrl = p.privacyUrl ?? "";
    this.fFields = (p.fields ?? []).map((f) => ({
      key: f.key,
      label: f.label,
      type: FIELD_TYPES.includes(f.type as AssistanceFieldType)
        ? (f.type as AssistanceFieldType)
        : "text",
      required: f.required,
      optionsText: (f.options ?? []).join(", "),
      help: f.help ?? "",
    }));
    // Write-only credentials: neither ever comes back in a Program, so the
    // editor starts blank. The token stays stored if left blank; the submit
    // URL must be re-entered on every save of a form programme.
    this.fSubmitUrl = "";
    this.fSubmitToken = "";
    this.saveError = "";
    this.editorOpen = true;
  }

  private closeEditor(): void {
    this.editorOpen = false;
    this.saveError = "";
  }

  private parsedMarkets(): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const raw of this.fMarkets.split(",")) {
      const code = raw.trim().toUpperCase();
      if (!code || seen.has(code)) continue;
      seen.add(code);
      out.push(code);
    }
    return out;
  }

  private updateField(index: number, patch: Partial<FieldDraft>): void {
    this.fFields = this.fFields.map((f, i) =>
      i === index ? { ...f, ...patch } : f,
    );
  }

  private addField(): void {
    this.fFields = [...this.fFields, emptyFieldDraft()];
  }

  private removeField(index: number): void {
    this.fFields = this.fFields.filter((_, i) => i !== index);
  }

  // Mirrors SaveProgram's own checks (assistance/service.go) so a producer
  // hears about a problem before the round trip; the server remains the
  // authority and its message is shown verbatim when it still refuses.
  private validate(): string {
    if (!this.fTitle.trim()) return "A title is required.";
    if (this.fEnabled && this.parsedMarkets().length === 0) {
      return (
        "Name the markets this programme is offered in before enabling it — " +
        "with no markets it is shown nowhere."
      );
    }
    if (this.fMode === "link" && !this.fProgramUrl.trim()) {
      return "Link mode needs a programme URL to send people to.";
    }
    if (this.fMode === "form") {
      if (!this.fSubmitUrl.trim()) {
        return (
          "Form mode needs the intake endpoint URL — without it the answers " +
          "would have nowhere to go."
        );
      }
      if (this.fFields.length === 0) {
        return "Form mode needs at least one field.";
      }
      for (const f of this.fFields) {
        if (!f.key.trim() || !f.label.trim()) {
          return "Every form field needs a key and a label.";
        }
      }
    }
    return "";
  }

  private async handleSave(e: Event): Promise<void> {
    e.preventDefault();
    this.saveError = this.validate();
    if (this.saveError) return;
    this.saving = true;
    try {
      // org comes from auth on the server, never from the body.
      const body: Record<string, unknown> = {
        id: this.editingId || undefined,
        productId: this.fProduct || undefined,
        enabled: this.fEnabled,
        markets: this.parsedMarkets(),
        title: this.fTitle.trim(),
        body: this.fBody,
        mode: this.fMode,
        programUrl: this.fProgramUrl.trim() || undefined,
        phone: this.fPhone.trim() || undefined,
        sponsor: this.fSponsor.trim() || undefined,
        privacyUrl: this.fPrivacyUrl.trim() || undefined,
      };
      if (this.fMode === "form") {
        body.fields = this.fFields.map((f) => ({
          key: f.key.trim(),
          label: f.label.trim(),
          type: f.type,
          required: f.required,
          options:
            f.type === "select"
              ? f.optionsText
                  .split(",")
                  .map((o) => o.trim())
                  .filter((o) => o !== "")
              : undefined,
          help: f.help.trim() || undefined,
        }));
        body.submitUrl = this.fSubmitUrl.trim();
        // Blank keeps the stored token (write-only; it cannot be read back).
        if (this.fSubmitToken.trim()) body.submitToken = this.fSubmitToken;
      }
      const saved = await api.put<AssistanceProgram>(
        "/assistance/programs",
        body,
      );
      this.programs = this.editingId
        ? this.programs.map((p) => (p.id === saved.id ? saved : p))
        : [saved, ...this.programs];
      this.editorOpen = false;
      toast(
        saved.enabled
          ? `Saved "${saved.title}" — live in ${(saved.markets ?? []).join(", ")}`
          : `Saved "${saved.title}" (disabled)`,
      );
    } catch (err) {
      this.saveError = errorMessage(err, "Failed to save the programme.");
    } finally {
      this.saving = false;
    }
  }

  private renderPrograms() {
    if (this.programs.length === 0) {
      return html`<div class="card empty">
        No assistance programmes configured yet.
      </div>`;
    }
    return html`
      <div class="card card--flush">
        <div class="cardhead">
          <h2>Programmes <small>· ${this.programs.length}</small></h2>
        </div>
        <div class="table-scroll"><table>
          <thead>
            <tr>
              <th>Scope</th>
              <th>Title</th>
              <th>Mode</th>
              <th>Markets</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            ${this.programs.map(
              (p) => html`
                <tr>
                  <td>
                    <b>${this.productName(p.productId)}</b>
                    ${p.productId
                      ? null
                      : html`<small>default for all products</small>`}
                  </td>
                  <td>
                    <b>${p.title}</b>
                    ${p.sponsor ? html`<small>${p.sponsor}</small>` : null}
                  </td>
                  <td>
                    <span class="badge badge--neutral">${p.mode}</span>
                  </td>
                  <td>
                    ${(p.markets ?? []).length > 0
                      ? p.markets!.join(", ")
                      : html`<span class="badge badge--bad"
                          >${icon("flag")} none — shown nowhere</span
                        >`}
                  </td>
                  <td>
                    ${p.enabled
                      ? html`<span class="badge badge--ok"
                          >${icon("verified")} Live</span
                        >`
                      : html`<span class="badge badge--neutral"
                          >${icon("schedule")} Disabled</span
                        >`}
                  </td>
                  <td>
                    ${this.canManage
                      ? html`<md-text-button @click=${() => this.openEdit(p)}>
                          Edit
                        </md-text-button>`
                      : null}
                  </td>
                </tr>
              `,
            )}
          </tbody>
        </table></div>
      </div>
    `;
  }

  private renderFieldBuilder() {
    return html`
      <h3>Application form fields</h3>
      <p class="hint">
        The fields your intake system expects. Every declared field is rendered
        and labelled on the tap page; answers to anything not declared here are
        refused.
      </p>
      ${this.fFields.map(
        (f, i) => html`
          <div class="field-card">
            <div class="field-card__head">
              <b>Field ${i + 1}</b>
              <md-text-button @click=${() => this.removeField(i)}>
                Remove
              </md-text-button>
            </div>
            <div class="form-row">
              <md-outlined-text-field
                label="Key"
                required
                supporting-text="The name your intake endpoint receives"
                .value=${f.key}
                @input=${(e: Event) =>
                  this.updateField(i, { key: inputValue(e) })}
              ></md-outlined-text-field>
              <md-outlined-text-field
                label="Label"
                required
                supporting-text="What the applicant is asked"
                .value=${f.label}
                @input=${(e: Event) =>
                  this.updateField(i, { label: inputValue(e) })}
              ></md-outlined-text-field>
            </div>
            <div class="form-row">
              <md-outlined-select
                label="Type"
                .value=${f.type}
                @change=${(e: Event) =>
                  this.updateField(i, {
                    type: inputValue(e) as AssistanceFieldType,
                  })}
              >
                ${FIELD_TYPES.map(
                  (t) => html`
                    <md-select-option value=${t}>
                      <div slot="headline">${t}</div>
                    </md-select-option>
                  `,
                )}
              </md-outlined-select>
              <label class="checkbox-row">
                <md-checkbox
                  ?checked=${f.required}
                  @change=${(e: Event) =>
                    this.updateField(i, { required: inputChecked(e) })}
                ></md-checkbox>
                <span>Required</span>
              </label>
            </div>
            ${f.type === "select"
              ? html`<md-outlined-text-field
                  label="Options (comma-separated)"
                  placeholder="Private insurance, Public insurance, Uninsured"
                  .value=${f.optionsText}
                  @input=${(e: Event) =>
                    this.updateField(i, { optionsText: inputValue(e) })}
                ></md-outlined-text-field>`
              : null}
            <md-outlined-text-field
              label="Help text (optional)"
              supporting-text="Shown under the field so nobody is asked for something unexplained"
              .value=${f.help}
              @input=${(e: Event) => this.updateField(i, { help: inputValue(e) })}
            ></md-outlined-text-field>
          </div>
        `,
      )}
      <div>
        <md-outlined-button @click=${() => this.addField()}>
          <span slot="icon">${icon("add")}</span>
          Add field
        </md-outlined-button>
      </div>

      <h3>Relay destination</h3>
      <p class="hint">
        Applications are posted straight to this endpoint and dropped — the
        platform keeps no copy, so a failed relay cannot be retried here. The
        applicant is shown the programme URL and phone above as the fallback.
      </p>
      <md-outlined-text-field
        label="Intake endpoint URL"
        required
        type="url"
        placeholder="https://intake.example.com/applications"
        supporting-text="Never displayed after saving — re-enter it whenever you save a form programme"
        .value=${this.fSubmitUrl}
        @input=${(e: Event) => (this.fSubmitUrl = inputValue(e))}
      ></md-outlined-text-field>
      <md-outlined-text-field
        label="Intake bearer token"
        type="password"
        autocomplete="off"
        supporting-text=${this.editingId
          ? "Write-only: it can be set but never read back. Leave blank to keep the current token."
          : "Write-only: it can be set but never read back."}
        .value=${this.fSubmitToken}
        @input=${(e: Event) => (this.fSubmitToken = inputValue(e))}
      ></md-outlined-text-field>
    `;
  }

  private renderEditor() {
    return html`
      <div class="card">
        <h2>${this.editingId ? "Edit programme" : "New programme"}</h2>
        <form class="editor" @submit=${(e: Event) => void this.handleSave(e)}>
          <md-outlined-select
            label="Applies to"
            .value=${this.fProduct}
            @change=${(e: Event) => (this.fProduct = inputValue(e))}
          >
            <md-select-option value="">
              <div slot="headline">Org-wide (default for all products)</div>
            </md-select-option>
            ${this.products.map(
              (p) => html`
                <md-select-option value=${p.ID}>
                  <div slot="headline">${p.Name}</div>
                </md-select-option>
              `,
            )}
          </md-outlined-select>

          <label class="enable-row">
            <md-switch
              ?selected=${this.fEnabled}
              @change=${(e: Event) =>
                (this.fEnabled = (e.target as MdSwitch).selected)}
            ></md-switch>
            <span>Programme is open</span>
          </label>

          <div class="markets-callout">
            <p>
              <b>Markets decide where this programme appears at all.</b>
              It is shown only on genuine taps, and only in the markets you
              name here (ISO country codes, e.g. <code>US, CA</code>). An
              empty list means it is shown <b>nowhere</b> — advertising
              prescription medicines to the public is prohibited in the EU, so
              nothing defaults to "everywhere".
            </p>
            <md-outlined-text-field
              label="Markets (comma-separated country codes)"
              placeholder="US, CA"
              .value=${this.fMarkets}
              @input=${(e: Event) => (this.fMarkets = inputValue(e))}
            ></md-outlined-text-field>
          </div>

          <md-outlined-text-field
            label="Title"
            required
            placeholder="Medicine Savings Program"
            .value=${this.fTitle}
            @input=${(e: Event) => (this.fTitle = inputValue(e))}
          ></md-outlined-text-field>
          <md-outlined-text-field
            label="Body"
            type="textarea"
            rows="3"
            supporting-text="Shown to the person holding the product, below the authenticity verdict"
            .value=${this.fBody}
            @input=${(e: Event) => (this.fBody = inputValue(e))}
          ></md-outlined-text-field>

          <md-outlined-select
            label="Mode"
            .value=${this.fMode}
            @change=${(e: Event) =>
              (this.fMode = inputValue(e) as AssistanceMode)}
          >
            ${(Object.keys(MODE_LABELS) as AssistanceMode[]).map(
              (m) => html`
                <md-select-option value=${m}>
                  <div slot="headline">${MODE_LABELS[m]}</div>
                </md-select-option>
              `,
            )}
          </md-outlined-select>

          <div class="form-row">
            <md-outlined-text-field
              label=${this.fMode === "link"
                ? "Programme URL"
                : "Programme URL (fallback if the relay fails)"}
              type="url"
              ?required=${this.fMode === "link"}
              placeholder="https://savings.example.com"
              .value=${this.fProgramUrl}
              @input=${(e: Event) => (this.fProgramUrl = inputValue(e))}
            ></md-outlined-text-field>
            <md-outlined-text-field
              label="Phone (optional)"
              type="tel"
              .value=${this.fPhone}
              @input=${(e: Event) => (this.fPhone = inputValue(e))}
            ></md-outlined-text-field>
          </div>

          <div class="form-row">
            <md-outlined-text-field
              label="Sponsor"
              supporting-text="Who receives the application — named on the form, so applicants know it goes to the manufacturer, not to us"
              .value=${this.fSponsor}
              @input=${(e: Event) => (this.fSponsor = inputValue(e))}
            ></md-outlined-text-field>
            <md-outlined-text-field
              label="Privacy policy URL"
              type="url"
              .value=${this.fPrivacyUrl}
              @input=${(e: Event) => (this.fPrivacyUrl = inputValue(e))}
            ></md-outlined-text-field>
          </div>

          ${this.fMode === "form" ? this.renderFieldBuilder() : null}

          ${this.saveError
            ? html`<p class="error-text">${this.saveError}</p>`
            : null}

          <div class="editor-actions">
            <md-text-button type="button" @click=${() => this.closeEditor()}>
              Cancel
            </md-text-button>
            <md-filled-button type="submit" ?disabled=${this.saving}>
              ${this.saving ? "Saving…" : "Save programme"}
            </md-filled-button>
          </div>
        </form>
      </div>
    `;
  }

  render() {
    return html`
      <app-shell active="/assistance">
        <div class="page">
          <div class="page-header">
            <div>
              <h1>Assistance programmes</h1>
              <p>
                A savings or patient-assistance programme appears on the tap
                page below the authenticity verdict — on genuine taps only, and
                only in the markets you name. Applications are relayed straight
                to your own intake system and never stored on this platform.
              </p>
            </div>
            ${this.canManage && !this.forbidden
              ? html`<md-filled-button
                  @click=${() => this.openCreate()}
                  ?disabled=${this.editorOpen && !this.editingId}
                >
                  <span slot="icon">${icon("add")}</span>
                  New programme
                </md-filled-button>`
              : null}
          </div>

          ${this.loading
            ? html`<div class="center-loading">
                <md-circular-progress indeterminate></md-circular-progress>
              </div>`
            : this.forbidden
              ? html`<div class="card empty">
                  Programme configuration is limited to organization admins.
                </div>`
              : this.error
                ? html`<p class="error-text">${this.error}</p>`
                : html`
                    ${this.editorOpen ? this.renderEditor() : null}
                    ${this.renderPrograms()}
                  `}
        </div>
      </app-shell>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "assistance-page": AssistancePage;
  }
}
